/**
 * Backup Management Panel
 * Admin only - Database backup list, creation, download, deletion, and verification
 */
import { useState, useEffect, useCallback } from 'react';
import { apiClient, BackupInfo, BackupVerifyResponse } from '../lib/api-client';

interface Props {
  className?: string;
}

export default function BackupManagementPanel({ className = '' }: Props) {
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [totalSize, setTotalSize] = useState('0B');
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deletingFilename, setDeletingFilename] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // Verification state
  const [verifyFilename, setVerifyFilename] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<BackupVerifyResponse | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isTestRestoring, setIsTestRestoring] = useState(false);

  const loadBackups = useCallback(async (page: number = currentPage) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.listBackups(page, pageSize);
      setBackups(response.backups);
      setTotalSize(response.total_size_human);
      setTotalCount(response.total_count);
      setTotalPages(response.total_pages);
      setCurrentPage(response.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'バックアップ一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    loadBackups(currentPage);
  }, [currentPage]);

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleCreateBackup = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const response = await apiClient.createBackup();
      setSuccessMessage(`バックアップを作成しました: ${response.filename} (${response.size_human})`);
      // 新規作成後は最初のページにリセット
      setCurrentPage(1);
      await loadBackups(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'バックアップの作成に失敗しました');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      await apiClient.downloadBackup(filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ダウンロードに失敗しました');
    }
  };

  const handleDeleteClick = (filename: string) => {
    setConfirmDelete(filename);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    setDeletingFilename(confirmDelete);
    setError(null);
    try {
      await apiClient.deleteBackup(confirmDelete);
      setSuccessMessage(`バックアップを削除しました: ${confirmDelete}`);
      setConfirmDelete(null);
      // 削除後に現在のページを再読み込み（最後の1件だった場合は前のページに戻る）
      const newPage = backups.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      } else {
        await loadBackups(currentPage);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'バックアップの削除に失敗しました');
    } finally {
      setDeletingFilename(null);
    }
  };

  const handleVerifyClick = async (filename: string) => {
    setVerifyFilename(filename);
    setVerifyResult(null);
    setVerifyError(null);
    setIsVerifying(true);

    try {
      const result = await apiClient.verifyBackup(filename, false);
      setVerifyResult(result);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : '検証に失敗しました');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleReVerify = async () => {
    if (!verifyFilename) return;

    setIsVerifying(true);
    setVerifyError(null);

    try {
      const result = await apiClient.verifyBackup(verifyFilename, false);
      setVerifyResult(result);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : '検証に失敗しました');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleTestRestore = async () => {
    if (!verifyFilename) return;

    setIsTestRestoring(true);
    setVerifyError(null);

    try {
      const result = await apiClient.verifyBackup(verifyFilename, true);
      setVerifyResult(result);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'テストリストアに失敗しました');
    } finally {
      setIsTestRestoring(false);
    }
  };

  const handleCloseVerifyModal = () => {
    setVerifyFilename(null);
    setVerifyResult(null);
    setVerifyError(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'manual': return { label: '手動', color: 'bg-blue-100 text-blue-800' };
      case 'auto': return { label: '自動', color: 'bg-green-100 text-green-800' };
      case 'pre_test': return { label: 'テスト前', color: 'bg-yellow-100 text-yellow-800' };
      case 'before_restore': return { label: 'リストア前', color: 'bg-purple-100 text-purple-800' };
      default: return { label: type, color: 'bg-gray-100 text-gray-800' };
    }
  };

  return (
    <div className={`p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">データベースバックアップ</h2>
          <p className="text-sm text-gray-600 mt-1">
            合計 {totalCount} 件 ({totalSize})
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => loadBackups(currentPage)}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded transition-colors disabled:opacity-50"
          >
            {isLoading ? '読込中...' : '更新'}
          </button>
          <button
            onClick={handleCreateBackup}
            disabled={isCreating || isLoading}
            className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white font-medium rounded transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isCreating ? (
              <>
                <span className="animate-spin">⏳</span>
                作成中...
              </>
            ) : (
              <>
                <span>📦</span>
                新規バックアップ
              </>
            )}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
          ⚠️ {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
          ✅ {successMessage}
        </div>
      )}

      {/* Backup List */}
      {isLoading && backups.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          読み込み中...
        </div>
      ) : backups.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          バックアップがありません
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-3 px-4 font-medium text-gray-600">ファイル名</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">種別</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">サイズ</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">作成日時</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">操作</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup) => {
                const typeInfo = getTypeLabel(backup.backup_type);
                return (
                  <tr key={backup.filename} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span>{backup.compressed ? '📦' : '📄'}</span>
                        <span className="font-mono text-sm">{backup.filename}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {backup.size_human}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatDate(backup.created_at)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleVerifyClick(backup.filename)}
                          className="px-3 py-1 bg-green-50 hover:bg-green-100 text-green-600 text-sm font-medium rounded transition-colors"
                          title="検証"
                        >
                          🔍 検証
                        </button>
                        <button
                          onClick={() => handleDownload(backup.filename)}
                          className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-medium rounded transition-colors"
                          title="ダウンロード"
                        >
                          ⬇️ DL
                        </button>
                        <button
                          onClick={() => handleDeleteClick(backup.filename)}
                          disabled={deletingFilename === backup.filename}
                          className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded transition-colors disabled:opacity-50"
                          title="削除"
                        >
                          {deletingFilename === backup.filename ? '...' : '🗑️ 削除'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-gray-50 rounded">
              <div className="text-sm text-gray-600">
                {totalCount} 件中 {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} 件を表示
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || isLoading}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="最初のページ"
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="前のページ"
                >
                  ‹
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="次のページ"
                >
                  ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || isLoading}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="最後のページ"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4">バックアップの削除</h3>
            <p className="text-gray-600 mb-2">以下のバックアップを削除しますか？</p>
            <p className="font-mono text-sm bg-gray-100 p-2 rounded mb-4 break-all">
              {confirmDelete}
            </p>
            <p className="text-red-600 text-sm mb-4">
              ⚠️ この操作は取り消せません
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deletingFilename !== null}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded transition-colors disabled:opacity-50"
              >
                {deletingFilename ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {verifyFilename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">バックアップの検証</h3>

            <p className="font-mono text-sm bg-gray-100 p-2 rounded mb-4 break-all">
              {verifyFilename}
            </p>

            {/* Loading state */}
            {isVerifying && (
              <div className="text-center py-4 text-gray-500">
                <span className="animate-spin inline-block mr-2">⏳</span>
                検証中...
              </div>
            )}

            {/* Error state */}
            {verifyError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                ⚠️ {verifyError}
              </div>
            )}

            {/* Verification result */}
            {verifyResult && !isVerifying && (
              <div className="space-y-4">
                {/* Overall status */}
                <div className={`p-3 rounded ${verifyResult.is_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{verifyResult.is_valid ? '✅' : '❌'}</span>
                    <div>
                      <div className={`font-bold ${verifyResult.is_valid ? 'text-green-800' : 'text-red-800'}`}>
                        {verifyResult.is_valid ? '有効なバックアップ' : '無効なバックアップ'}
                      </div>
                      <div className="text-sm text-gray-600">
                        サイズ: {verifyResult.file_size_human}
                      </div>
                    </div>
                  </div>
                </div>

                {/* File checks */}
                <div className="border rounded p-3">
                  <h4 className="font-medium text-gray-700 mb-2">ファイルチェック</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <span>{verifyResult.file_checks.exists ? '✅' : '❌'}</span>
                      <span>ファイル存在</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>{verifyResult.file_checks.gzip_valid === null ? '➖' : verifyResult.file_checks.gzip_valid ? '✅' : '❌'}</span>
                      <span>圧縮データ破損なし{verifyResult.file_checks.gzip_valid === null && ' (非圧縮ファイル)'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>{verifyResult.file_checks.sql_header_valid ? '✅' : '❌'}</span>
                      <span>PostgreSQLダンプ形式</span>
                    </div>
                  </div>
                </div>

                {/* Test restore result */}
                {verifyResult.restore_test && (
                  <div className={`border rounded p-3 ${verifyResult.restore_test.success ? 'bg-green-50' : 'bg-yellow-50'}`}>
                    <h4 className="font-medium text-gray-700 mb-2">テストリストア結果</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <span>{verifyResult.restore_test.success ? '✅' : '⚠️'}</span>
                      <span>{verifyResult.restore_test.message}</span>
                    </div>
                    {verifyResult.restore_test.table_counts && (
                      <div className="mt-2">
                        <h5 className="text-sm font-medium text-gray-600 mb-1">テーブル別レコード数:</h5>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          {Object.entries(verifyResult.restore_test.table_counts).map(([table, count]) => (
                            <div key={table} className="flex justify-between bg-white p-1 rounded">
                              <span className="font-mono">{table}</span>
                              <span className="font-bold">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Test restore button - show only when not yet tested */}
                {!verifyResult.restore_test && (
                  <div className="border rounded p-3">
                    <button
                      onClick={handleTestRestore}
                      disabled={isTestRestoring || !verifyResult.is_valid}
                      className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isTestRestoring ? '🔄 テストリストア実行中...' : '🧪 テストリストアを実行'}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      テストDBへの復元を試行し、各テーブルのレコード数を確認します
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              {verifyResult && !isVerifying && !isTestRestoring && (
                <button
                  onClick={handleReVerify}
                  disabled={isVerifying || isTestRestoring}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded transition-colors disabled:opacity-50"
                >
                  再検証
                </button>
              )}
              <button
                onClick={handleCloseVerifyModal}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 p-4 bg-blue-50 rounded border border-blue-200">
        <h3 className="font-medium text-blue-800 mb-2">バックアップについて</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 手動バックアップは最新10件まで保持されます</li>
          <li>• 自動バックアップは最新10件まで保持されます</li>
          <li>• バックアップはgzip圧縮されています</li>
          <li>• リストアはサーバー管理者のみ実行できます</li>
        </ul>
      </div>
    </div>
  );
}
